/**
 * Geometria e vocabulario do odontograma.
 *
 * Fica fora do componente de proposito: e a parte clinicamente sensivel
 * (qual face e mesial, qual e vestibular) e precisa ser testavel sem montar Vue.
 *
 * Notacao FDI. O primeiro digito do numero e o quadrante, o segundo e a posicao
 * do dente contando a partir da linha media.
 */

export const DENTITIONS = {
  permanent: {
    upper: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
    lower: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
  },
  deciduous: {
    upper: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
    lower: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
  },
};

export const FACE_LABELS = {
  V: 'Vestibular',
  L: 'Lingual',
  P: 'Palatina',
  M: 'Mesial',
  D: 'Distal',
  O: 'Oclusal',
  I: 'Incisal',
};

/**
 * Condicao do dente inteiro. `fill: null` significa que as faces mandam na cor.
 * `mark` indica um sinal grafico adicional: cor nunca pode ser o unico canal.
 */
export const TOOTH_STATUS = {
  present: { label: 'Presente', fill: null, mark: null },
  absent: { label: 'Ausente', fill: '#b9c0c7', mark: 'cross' },
  root: { label: 'Resto radicular', fill: '#ffe8cc', mark: 'root' },
  implant: { label: 'Implante', fill: '#f1f3f5', mark: 'implant' },
  crown: { label: 'Coroa', fill: '#fff3bf', mark: 'ring' },
  prosthesis: { label: 'Prótese', fill: '#e5dbff', mark: 'ring' },
};

/**
 * Condicao da face. Vermelho = a fazer, azul = realizado (convencao brasileira).
 * `hatch` existe para o encoding sobreviver a daltonismo.
 */
export const FACE_STATUS = {
  healthy: { label: 'Hígido', fill: null, hatch: false },
  caries: { label: 'Cárie', fill: '#e03131', hatch: false },
  restored: { label: 'Restaurado', fill: '#1c7ed6', hatch: true },
  sealant: { label: 'Selante', fill: '#40c057', hatch: true },
  fracture: { label: 'Fratura', fill: '#f76707', hatch: false },
};

/**
 * Endodontia no dente (paralelo a faces/status). Nao confundir com `root`
 * (resto radicular). Cor + letra "E" — cor nunca e o unico canal.
 */
export const ENDO_STATUS = {
  none: { label: 'Nenhum', fill: null, mark: null },
  indicated: { label: 'Canal indicado', fill: '#c92a2a', mark: 'endo' },
  in_progress: { label: 'Canal em andamento', fill: '#e67700', mark: 'endo' },
  completed: { label: 'Canal obturado', fill: '#2f9e44', mark: 'endo' },
};

/** Alinha status de endo com status de procedimento cobravel. */
export const ENDO_TO_PROCEDURE_STATUS = {
  indicated: 'planned',
  in_progress: 'in_progress',
  completed: 'done',
};

/**
 * Marcacao rapida da barra do odontograma. Deriva das tabelas acima para o
 * rotulo nunca divergir entre a barra e o modal.
 */
export const QUICK_MODES = [
  { key: 'caries', label: FACE_STATUS.caries.label },
  { key: 'restored', label: FACE_STATUS.restored.label },
  { key: 'absent', label: TOOTH_STATUS.absent.label },
  { key: 'healthy', label: FACE_STATUS.healthy.label },
];

/**
 * Legenda unica para a UI: faces, condicoes de dente e endodontia.
 * Hígido entra como referencia; present/none nao aparecem.
 *
 * @returns {Array<{ group: string, key: string, label: string, fill: string|null, mark: string|null, hatch?: boolean }>}
 */
export function buildLegend() {
  const faces = ['healthy', 'caries', 'restored', 'sealant', 'fracture'].map((key) => ({
    group: 'face',
    key,
    label: FACE_STATUS[key].label,
    fill: FACE_STATUS[key].fill,
    mark: null,
    hatch: FACE_STATUS[key].hatch,
  }));

  const teeth = ['absent', 'root', 'implant', 'crown', 'prosthesis'].map((key) => ({
    group: 'tooth',
    key,
    label: TOOTH_STATUS[key].label,
    fill: TOOTH_STATUS[key].fill,
    mark: TOOTH_STATUS[key].mark,
  }));

  const endo = ['indicated', 'in_progress', 'completed'].map((key) => ({
    group: 'endo',
    key,
    label: ENDO_STATUS[key].label,
    fill: ENDO_STATUS[key].fill,
    mark: ENDO_STATUS[key].mark,
  }));

  return [...faces, ...teeth, ...endo];
}

export function hasActiveEndo(entry) {
  const status = entry?.endo?.status;
  return Boolean(status && status !== 'none' && ENDO_STATUS[status]);
}
const VIEWBOX = { width: 900, height: 262 };
const CELL = 44;
const MARGIN = 40;
// O vao entre as arcadas so precisa ser grande o bastante para as duas lerem
// como arcadas separadas. Mais que isso e area morta no meio do desenho.
const ROW_Y = { upper: 56, lower: 178 };

/**
 * Quadrantes do lado direito do paciente. Sao desenhados na metade esquerda da
 * tela, entao neles a face mesial (a que aponta para a linha media) fica a
 * direita da celula. Nos quadrantes da esquerda e o contrario.
 *
 * 1 e 4: permanentes direitos. 5 e 8: deciduos direitos.
 */
const RIGHT_SIDE_QUADRANTS = [1, 4, 5, 8];

/**
 * @param {number|string} number numero FDI do dente
 * @param {'upper'|'lower'} arch
 */
export function faceCodes(number, arch) {
  const digits = String(number);
  const quadrant = Number(digits[0]);
  const position = Number(digits[1]);
  const rightIsMesial = RIGHT_SIDE_QUADRANTS.includes(quadrant);

  return {
    top: arch === 'upper' ? 'V' : 'L',
    bottom: arch === 'upper' ? 'P' : 'V',
    left: rightIsMesial ? 'D' : 'M',
    right: rightIsMesial ? 'M' : 'D',
    // Incisivos (1, 2) e caninos (3) tem borda incisal; pre-molares e molares,
    // superficie oclusal.
    center: position <= 3 ? 'I' : 'O',
  };
}

/**
 * Divide a celula do dente em 5 regioes: quatro trapezios nas bordas e um
 * quadrado central.
 */
function polygons(x, y, size, codes) {
  const t = size / 3;
  const x1 = x + size;
  const y1 = y + size;
  const ix0 = x + t;
  const iy0 = y + t;
  const ix1 = x + size - t;
  const iy1 = y + size - t;

  return [
    { code: codes.top, points: `${x},${y} ${x1},${y} ${ix1},${iy0} ${ix0},${iy0}` },
    { code: codes.right, points: `${x1},${y} ${x1},${y1} ${ix1},${iy1} ${ix1},${iy0}` },
    { code: codes.bottom, points: `${x},${y1} ${x1},${y1} ${ix1},${iy1} ${ix0},${iy1}` },
    { code: codes.left, points: `${x},${y} ${x},${y1} ${ix0},${iy1} ${ix0},${iy0}` },
    { code: codes.center, points: `${ix0},${iy0} ${ix1},${iy0} ${ix1},${iy1} ${ix0},${iy1}` },
  ];
}

/**
 * @param {'permanent'|'deciduous'} dentition
 * @returns {{viewBox: string, cell: number, teeth: Array<object>}}
 */
export function buildLayout(dentition = 'permanent') {
  const rows = DENTITIONS[dentition] ?? DENTITIONS.permanent;
  const pitch = (VIEWBOX.width - 2 * MARGIN) / rows.upper.length;
  const teeth = [];

  for (const arch of ['upper', 'lower']) {
    rows[arch].forEach((number, index) => {
      const x = MARGIN + index * pitch + (pitch - CELL) / 2;
      const y = ROW_Y[arch];
      const digits = String(number);

      teeth.push({
        number: digits,
        arch,
        quadrant: Number(digits[0]),
        position: Number(digits[1]),
        x,
        y,
        size: CELL,
        labelX: x + CELL / 2,
        labelY: arch === 'upper' ? y - 10 : y + CELL + 20,
        faces: polygons(x, y, CELL, faceCodes(number, arch)),
      });
    });
  }

  return {
    viewBox: `0 0 ${VIEWBOX.width} ${VIEWBOX.height}`,
    cell: CELL,
    // A linha media acompanha a altura do desenho: hardcodar isto no componente
    // fazia a linha sobrar quando a geometria mudava.
    midline: { x: VIEWBOX.width / 2, y1: 18, y2: VIEWBOX.height - 18 },
    teeth,
  };
}

/**
 * Nome legivel do quadrante, para cabecalho de modal e leitores de tela.
 */
export function quadrantLabel(number) {
  return {
    1: 'Superior direito',
    2: 'Superior esquerdo',
    3: 'Inferior esquerdo',
    4: 'Inferior direito',
    5: 'Superior direito (decidua)',
    6: 'Superior esquerdo (decidua)',
    7: 'Inferior esquerdo (decidua)',
    8: 'Inferior direito (decidua)',
  }[Number(String(number)[0])] ?? '';
}

/**
 * Marcação rápida com toggle: repetir o mesmo modo no mesmo alvo desfaz.
 *
 * @param {{ status: string, faces?: Record<string, string> }} entry
 * @returns {boolean} true quando o estado do dente mudou
 */
export function applyQuickMark(entry, faceCode, mode, fallbackFaceCode) {
  if (!entry.faces) {
    entry.faces = {};
  }

  if (mode === 'absent') {
    if (entry.status === 'absent') {
      entry.status = 'present';
      entry.faces = {};
      return true;
    }

    entry.status = 'absent';
    entry.faces = {};
    return true;
  }

  if (mode === 'healthy') {
    if (faceCode) {
      if (entry.status === 'present' && !entry.faces[faceCode]) {
        return false;
      }

      entry.status = 'present';
      delete entry.faces[faceCode];
      return true;
    }

    const hadMarking = entry.status !== 'present' || Object.keys(entry.faces).length > 0;
    if (!hadMarking) {
      return false;
    }

    entry.status = 'present';
    entry.faces = {};
    return true;
  }

  const targetFace = faceCode ?? fallbackFaceCode;
  entry.status = 'present';

  if (entry.faces[targetFace] === mode) {
    delete entry.faces[targetFace];
    return true;
  }

  entry.faces[targetFace] = mode;
  return true;
}

export function centerFaceCode(layout, toothNumber) {
  const tooth = layout.teeth.find((item) => item.number === String(toothNumber));
  return tooth ? tooth.faces[4].code : 'O';
}
